import { FieldValue, fieldControlClass, fieldDescribedBy } from "./FieldValue";
import {
  convertLocalDateTimeToUTC,
  convertUTCDateTimeToLocal,
  toDate,
  toDateTimeInputValue,
  toLocalDateTimeValue,
} from "./formatDate";
import { DateTimeFieldProps } from "./DateTimeField.types";
import { DateInput } from "./DateInput";

/**
 * Date+time editable field, on the shared date picker with its time list - which opens
 * on the hour, where the browser's own picker opens on the current minute and can't be
 * told otherwise.
 */
export function DateTimeField({
  label,
  name,
  value,
  onChange,
  disabled,
  required,
  storedAsLocal = false,
  min,
  error,
  className,
  helpText,
  preview = false,
}: DateTimeFieldProps) {
  // The record holds UTC unless told otherwise; the picker shows and takes local time.
  const inputValue = toDateTimeInputValue(
    storedAsLocal ? toDate(value) : convertUTCDateTimeToLocal(value)?.toDate()
  );

  const handleChange = (picked: string) =>
    onChange(
      picked
        ? storedAsLocal
          ? toLocalDateTimeValue(picked)
          : convertLocalDateTimeToUTC(picked)
        : ""
    );

  // An empty read-only field is an empty grey box, like any other; the picker's
  // placeholder would read as a value to fill in. The designer keeps the placeholder -
  // it is what the field will look like.
  if (disabled && !inputValue && !preview) {
    return (
      <FieldValue label={label} htmlFor={name} required={required} helpText={helpText} error={error} className={className}>
        <input
          id={name}
          aria-describedby={fieldDescribedBy(name, helpText, error)}
          name={name}
          type="text"
          className={fieldControlClass("form-control", error)}
          value=""
          disabled
          readOnly
        />
      </FieldValue>
    );
  }

  return (
    <FieldValue label={label} htmlFor={name} required={required} helpText={helpText} error={error} className={className}>
      <DateInput
        id={name}
        value={inputValue || null}
        onChange={handleChange}
        showTime
        min={min}
        disabled={disabled}
        required={required}
        className={fieldControlClass("form-control", error)}
        ariaDescribedBy={fieldDescribedBy(name, helpText, error)}
        ariaInvalid={Boolean(error)}
      />
    </FieldValue>
  );
}
